import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { IconOnlyButton } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Heading, HeadingLevel } from '@/components/common/MarkUp';
import { Slider } from '@/components/common/SliderCommon/Slider';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { Stack } from '@/components/layout/Stack';
import { blocksToText } from '@/lib/sanityUtils';

import type { SanityCarouselType } from '@/types/sanity';

export interface CarouselProps extends SanityCarouselType {}

const sliderSettings = {
  infinite: false,
  speed: 500,
  slidesToScroll: 1,
  slidesToShow: 3.3,

  responsive: [
    {
      breakpoint: 360,
      settings: {
        slidesToShow: 1,
      },
    },
    {
      breakpoint: 750,
      settings: {
        slidesToShow: 1.3,
      },
    },
    {
      breakpoint: 1300,
      settings: {
        slidesToShow: 3.1,
      },
    },
  ],
};

const Carousel = ({
  heading,
  carouselItems,
  backgroundColour: { token: backgroundColourToken },
  trackingTag,
}: CarouselProps) => {
  const headingPlainText = blocksToText(heading);

  return (
    <Section
      backgroundColour={backgroundColourToken || '--background-cool-base'}
      verticalPadding
    >
      <OuterWrapper>
        <HeadingLevel>
          <HeadingContainer>
            <CaptionHeadingDescription heading={heading} />
          </HeadingContainer>
          <HeadingLevel>
            <StyledSlider
              settings={sliderSettings}
              heading={headingPlainText}
              nextArrow={
                <IconOnlyButton
                  aria-label="next slides"
                  className={
                    trackingTag?.blockName &&
                    `tracking-${trackingTag.blockName}__${trackingTag.blockAction}-next`
                  }
                  componentTheme="light"
                />
              }
              prevArrow={
                <IconOnlyButton
                  aria-label="previous slides"
                  className={
                    trackingTag?.blockName &&
                    `tracking-${trackingTag.blockName}__${trackingTag.blockAction}-previous`
                  }
                  componentTheme="light"
                  icon="ArrowLeft"
                />
              }
              slideItems={carouselItems}
              slideItemTitleKey="heading"
            >
              {carouselItems.map((carouselItem) => (
                <CarouselItem key={carouselItem._key}>
                  <Stack spacing="--spacing-medium">
                    <Heading size="heading-md">{carouselItem.heading}</Heading>
                    <PortableText value={carouselItem.text} />
                  </Stack>
                </CarouselItem>
              ))}
            </StyledSlider>
          </HeadingLevel>
        </HeadingLevel>
      </OuterWrapper>
    </Section>
  );
};

const StyledSlider = styled(Slider)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  padding-top: var(--spacing-500);

  ${screen.md} {
    padding-top: var(--spacing-700);
  }

  .slick-slide {
    padding-right: 20px;
    width: 260px;
    > div {
      height: 100%;
    }

    ${screen.md} {
      width: 358px;
      flex-grow: 1;
    }
  }

  .slick-track {
    height: 100%;
  }

  .slick-arrow {
    top: -20px;

    ${screen.sm} {
      top: -5px;
    }
  }

  .slick-next {
    left: 50px;
  }

  .slick-prev {
    right: auto;
    left: 0;
  }

  .slick-prev,
  .slick-next {
    position: absolute;
    display: block;
    cursor: pointer;
    padding: 0;
    border-radius: var(--radius-full);
    outline-offset: 2px;
    width: fit-content;
  }
`;

const CarouselItem = styled.div`
  background-color: var(--surface-primary-subtle);
  padding: var(--spacing-500) var(--spacing-300);
  border-radius: var(--radius-s);
  display: flex !important;
  flex-direction: column;
  min-height: 208px;
  height: 100%;

  ${screen.md} {
    min-height: 220px;
    padding: var(--spacing-300);
  }
`;

const OuterWrapper = styled.div`
  padding-left: var(--layout-gutter);

  ${screen.md} {
    padding-left: 0;
  }
`;

const HeadingContainer = styled.div`
  max-width: 320px;
  margin-bottom: var(--spacing-700);
  ${screen.sm} {
    margin-bottom: var(--spacing-400);
    max-width: 460px;
  }
  ${screen.md} {
    margin-bottom: var(--spacing-500);
    max-width: none;
  }
`;

export { Carousel };
